import * as Sentry from '@sentry/browser';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AllUsersForAddUserDialogDocument,
  FilterForUsers,
  UsersByRoleIdDocument,
  UsersCountDocument,
  useAllRolesQuery,
  useAssignRoleToUsersMutation,
} from '@/__generated__/graphql';
import { PageLoader } from '@/components/shared/PageLoader';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { NavigationListItem } from '@/types';
import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AddUserDialog } from '../Dialogs/AddUserDialog';
import { RoleDetail } from './RoleDetail';
import { NavigationPanel } from '../shared/NavigationPanel';
import { ActionButton, PanelBox, PanelTitle } from '../styles';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';

type Role = {
  id: string;
  auth0Id: string;
  name: string;
  description: string;
};

export const RolesPanel = () => {
  const { user } = useAuth0();

  const [addUserModal, setAddUserModal] = useState(false);

  const { loading, data: userRolesData } = useAllRolesQuery();
  const [selectedRole, setSelectedRole] = useState<Role | undefined>();
  const { leftDrawerWidth } = useDrawerWidths();

  const queryVariables: FilterForUsers = {
    excludeRoleIds: [selectedRole?.id || ''],
    connection: undefined,
    roleId: undefined,
    q: undefined,
  };

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [checked, setChecked] = useState<string[]>([]);

  const handleToggleAddUserModal = useCallback(() => {
    if (addUserModal) setChecked([]);
    setAddUserModal(!addUserModal);
  }, [addUserModal]);

  const handleChecked = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const [assignRoleToUsersMutation] = useAssignRoleToUsersMutation();
  const handleAddUsers = useCallback(() => {
    if (checked && selectedRole?.id) {
      assignRoleToUsersMutation({
        variables: {
          input: {
            roleId: selectedRole?.id,
            userIds: checked,
          },
        },
        refetchQueries: [
          AllUsersForAddUserDialogDocument,
          UsersByRoleIdDocument,
          UsersCountDocument,
        ],
        onCompleted: (response) => {
          if (response) {
            handleToggleAddUserModal();
            showSnackBar('User has been added to this role successfully', 'success');
          }
        },
        onError: (error) => {
          Sentry.captureMessage(`Failed to assign role to user: ${error.message}`, {
            user: { sub: user?.sub },
            extra: {
              roleId: selectedRole?.id,
              userIds: JSON.stringify(checked),
            },
          });
          handleToggleAddUserModal();
          showSnackBar(`Failed to assign role to user: ${error.message}`, 'error');
        },
      });
    }
  }, [
    assignRoleToUsersMutation,
    checked,
    handleToggleAddUserModal,
    selectedRole?.id,
    showSnackBar,
    user?.sub,
  ]);

  useEffect(() => {
    if (!selectedRole && !loading && userRolesData?.allRoles[0]) {
      setSelectedRole(userRolesData?.allRoles[0] as Role | undefined);
    }
  }, [userRolesData, loading, selectedRole]);

  const handleRoleClick = (role: Role) => {
    setSelectedRole(role);
  };

  const handleFormatting = useCallback((name: string) => {
    return name
      .replace(/-/g, ' ')
      .replace(/(\w+)/g, (match) => match.charAt(0).toUpperCase() + match.slice(1));
  }, []);

  const items = useMemo(() => {
    return (userRolesData?.allRoles ?? [])
      .map((role) => {
        if (role) {
          const item: NavigationListItem = {
            id: role.auth0Id,
            value: role.name,
            label: handleFormatting(role.name),
            handleClick: () => {
              handleRoleClick(role as Role);
            },
          };
          return item;
        }
        return undefined;
      })
      .filter((role): role is NavigationListItem => !!role);
  }, [handleFormatting, userRolesData?.allRoles]);

  if (loading) {
    return (
      <Box sx={{ position: 'relative', top: '107px' }}>
        <PageLoader />
      </Box>
    );
  }

  return (
    <Stack direction="row" height="100%">
      <NavigationPanel itemType="Role" items={items} selectedItemId={selectedRole?.auth0Id ?? ''} />
      <PanelBox width={`calc(100vw - ${leftDrawerWidth}px)`}>
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <div>
              <PanelTitle data-testid="role-title">{selectedRole?.name}</PanelTitle>
              <Typography data-testid="role-description" variant="body1" color="#425265">
                {selectedRole?.description}
              </Typography>
            </div>
            <ActionButton
              variant="text"
              size="medium"
              startIcon={<AddCircleRoundedIcon />}
              onClick={handleToggleAddUserModal}
              data-testid="add-user-button"
            >
              Add User To This Role
            </ActionButton>
          </Stack>
          <RoleDetail
            selectedRoleID={selectedRole?.id || ''}
            selectedRoleName={selectedRole?.name || ''}
            selectedRole={selectedRole?.auth0Id}
          />
          {addUserModal && (
            <AddUserDialog
              open={addUserModal}
              handleCheckedUsers={handleChecked}
              handleAddUsers={handleAddUsers}
              handleToggle={handleToggleAddUserModal}
              checked={checked}
              queryVariable={queryVariables}
            />
          )}
        </Box>
      </PanelBox>
    </Stack>
  );
};
